import { Component, OnInit } from "@angular/core"
import { CommonModule } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { ActivatedRoute, Router } from "@angular/router"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatSelectModule } from "@angular/material/select"
import { MatIconModule } from "@angular/material/icon"
import { PageHeaderComponent } from "../../shared/page-header/page-header.component"
import { DataLoaderComponent } from "../../shared/data-loader/data-loader.component"
import { AuthService } from "../../services/auth.service"
import { PermissionService } from "../../services/permission.service"
import {
  SupportAttachment,
  SupportCategory,
  SupportService,
  SupportTicket,
} from "../../services/support.service"

@Component({
  selector: "app-help",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    PageHeaderComponent,
    DataLoaderComponent,
  ],
  templateUrl: "./help.component.html",
  styleUrl: "./help.component.css",
})
export class HelpComponent implements OnInit {
  categories: SupportCategory[] = []
  myTickets: SupportTicket[] = []
  loading = false
  isSubmitting = false
  showForm = false

  selectedCategory = ""
  subject = ""
  description = ""
  stationId: number | null = null
  formError = ""
  successMessage = ""
  referenceType: "order" | "commercial_request" | null = null
  referenceId: number | null = null
  referenceLabel = ""

  pendingAttachments: SupportAttachment[] = []
  uploadingFiles = false
  uploadError = ""

  filterStatus = ""
  totalTickets = 0
  currentPage = 1
  itemsPerPage = 20

  statusOptions = [
    { value: "", label: "All Tickets" },
    { value: "open", label: "Open" },
    { value: "in_progress", label: "In Progress" },
    { value: "resolved", label: "Resolved" },
    { value: "closed", label: "Closed" },
  ]

  readonly ALLOWED_TYPES = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/gif",
    "image/webp",
    "application/pdf",
    "text/plain",
  ]
  readonly MAX_FILE_SIZE = 10 * 1024 * 1024
  readonly MAX_FILES = 5

  constructor(
    private supportService: SupportService,
    private authService: AuthService,
    private permissionService: PermissionService,
    private route: ActivatedRoute,
    public router: Router,
  ) {}

  /** Advertiser account: no staff role and no current station assignment. */
  get isUser(): boolean {
    return !this.isAdmin && !this.permissionService.isStationStaffAccount()
  }

  get isAdmin(): boolean {
    return this.authService.getCurrentUserInfo()?.role?.toLowerCase() === "admin"
  }

  /** Station staff by login role or by a current station assignment (raises tickets to admin). */
  get isStationStaff(): boolean {
    return this.permissionService.isStationStaffAccount()
  }

  get canCreateTicket(): boolean {
    if (this.isAdmin) return false
    return this.isUser || this.isStationStaff || this.supportService.permissions.can_create
  }

  get selectedCategoryObj(): SupportCategory | undefined {
    return this.categories.find((cat) => cat.value === this.selectedCategory)
  }

  get totalPages(): number {
    return Math.ceil(this.totalTickets / this.itemsPerPage) || 0
  }

  get paginationStart(): number {
    if (this.totalTickets === 0) return 0
    return (this.currentPage - 1) * this.itemsPerPage + 1
  }

  get paginationEnd(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalTickets)
  }

  ngOnInit(): void {
    this.supportService.refreshPermissionsFromRole()
    if (this.isAdmin) {
      this.router.navigate(["/help/tickets"])
      return
    }

    this.loadCategories()
    this.showForm = false
    this.loadTickets()

    this.route.queryParams.subscribe((params) => {
      if (params["prefill"] === "true") {
        this.openForm()
        this.selectedCategory = params["category"] || ""
        this.subject = params["subject"] || ""
        this.referenceType = params["reference_type"] || null
        this.referenceId = params["reference_id"] ? +params["reference_id"] : null
        this.referenceLabel = params["reference_label"] || ""
      }
    })
  }

  openForm(): void {
    this.showForm = true
    this.successMessage = ""
    this.formError = ""
    this.uploadError = ""
    setTimeout(() => {
      document.querySelector(".help-form-card")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      })
    }, 50)
  }

  closeForm(): void {
    this.resetForm()
    this.showForm = false
  }

  resetForm(): void {
    this.selectedCategory = ""
    this.subject = ""
    this.description = ""
    this.referenceType = null
    this.referenceId = null
    this.referenceLabel = ""
    this.pendingAttachments = []
    this.formError = ""
    this.uploadError = ""
    this.successMessage = ""
  }

  loadCategories(): void {
    this.supportService.getCategories("create").subscribe({
      next: (categories) => {
        this.categories = categories ?? []
      },
      error: () => {
        this.categories = []
      },
    })
  }

  loadTickets(): void {
    this.loading = true
    this.supportService
      .listTickets({
        view: "mine",
        status: this.filterStatus || undefined,
        limit: this.itemsPerPage,
        offset: (this.currentPage - 1) * this.itemsPerPage,
      })
      .subscribe({
        next: (res) => {
          // view=mine is filtered server-side (COUNT and LIMIT), so the page and total are exact.
          const tickets = res?.tickets ?? []
          this.myTickets = tickets
          this.totalTickets = res?.pagination?.total ?? tickets.length
          this.loading = false
        },
        error: () => {
          this.myTickets = []
          this.totalTickets = 0
          this.loading = false
        },
      })
  }

  onFilterChange(): void {
    this.currentPage = 1
    this.loadTickets()
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return
    if (page === this.currentPage) return
    this.currentPage = page
    this.loadTickets()
    document.querySelector(".help-tickets")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    })
  }

  getPaginationPages(): number[] {
    const pages: number[] = []
    const maxVisible = 5
    if (this.totalPages <= maxVisible) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i)
      }
    } else {
      const start = Math.max(1, Math.min(this.currentPage - 2, this.totalPages - maxVisible + 1))
      const end = Math.min(this.totalPages, start + maxVisible - 1)
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
    }
    return pages
  }

  submitTicket(): void {
    this.formError = ""

    if (!this.selectedCategory) {
      this.formError = "Please select an issue category."
      return
    }
    if (!this.subject.trim()) {
      this.formError = "Please enter a subject."
      return
    }
    if (!this.description.trim() || this.description.trim().length < 10) {
      this.formError = "Please describe your issue in at least 10 characters."
      return
    }

    this.isSubmitting = true
    const data: {
      category: string
      subject: string
      description: string
      reference_type?: "order" | "commercial_request"
      reference_id?: number
      attachment_ids?: number[]
    } = {
      category: this.selectedCategory,
      subject: this.subject.trim(),
      description: this.description.trim(),
    }
    if (this.referenceType) data.reference_type = this.referenceType
    if (this.referenceId) data.reference_id = this.referenceId
    if (this.pendingAttachments.length > 0) {
      data.attachment_ids = this.pendingAttachments.map((a) => a.id)
    }

    this.supportService.createTicket(data).subscribe({
      next: (res) => {
        const ticketNumber = res?.ticket?.ticket_number || res?.ticket_number
        this.successMessage = ticketNumber
          ? `Your request ${ticketNumber} has been submitted. We'll get back to you as soon as possible.`
          : "Your request has been submitted. We'll get back to you as soon as possible."
        this.selectedCategory = ""
        this.subject = ""
        this.description = ""
        this.stationId = null
        this.referenceType = null
        this.referenceId = null
        this.referenceLabel = ""
        this.pendingAttachments = []
        this.uploadError = ""
        this.isSubmitting = false
        this.filterStatus = ""
        this.currentPage = 1
        this.showForm = false
        this.loadTickets()
      },
      error: (err) => {
        this.formError =
          err?.error?.error || err?.error?.message || "Unable to submit your request. Please try again."
        this.isSubmitting = false
      },
    })
  }

  getCategoryLabel(category: string): string {
    return this.categories.find((cat) => cat.value === category)?.label || category
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      open: "Open",
      in_progress: "In Progress",
      resolved: "Resolved",
      closed: "Closed",
    }
    return labels[status] || status
  }

  openTicket(ticket: SupportTicket): void {
    this.router.navigate(["/help/tickets", ticket.id])
  }

  onAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    if (!input.files) return
    this.uploadFiles(Array.from(input.files))
    input.value = ""
  }

  onAttachmentDrop(event: DragEvent): void {
    event.preventDefault()
    const files = Array.from(event.dataTransfer?.files || [])
    this.uploadFiles(files)
  }

  removeAttachment(id: number): void {
    this.pendingAttachments = this.pendingAttachments.filter((a) => a.id !== id)
  }

  isImage(fileType: string): boolean {
    return (fileType || "").startsWith("image/")
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + " B"
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB"
    return (bytes / 1048576).toFixed(1) + " MB"
  }

  private uploadFiles(files: File[]): void {
    for (const file of files) {
      if (!this.ALLOWED_TYPES.includes(file.type)) {
        this.uploadError = `${file.name}: File type not allowed. Use PNG, JPG, GIF, WebP, PDF, or TXT.`
        continue
      }
      if (file.size > this.MAX_FILE_SIZE) {
        this.uploadError = `${file.name}: File too large. Max 10MB.`
        continue
      }
      if (this.pendingAttachments.length >= this.MAX_FILES) {
        this.uploadError = `Maximum ${this.MAX_FILES} files allowed.`
        break
      }

      this.uploadingFiles = true
      this.supportService.uploadAttachment(file).subscribe({
        next: (res) => {
          if (res?.attachment) {
            this.pendingAttachments.push(res.attachment)
          }
          this.uploadingFiles = false
          this.uploadError = ""
        },
        error: () => {
          this.uploadingFiles = false
          this.uploadError = `Failed to upload ${file.name}.`
        },
      })
    }
  }
}
